import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  FileText, 
  FileSpreadsheet, 
  FileJson, 
  Download, 
  Settings, 
  Eye,
  CheckSquare,
  Square
} from 'lucide-react';

const API_BASE = '';

export default function ReportBuilderPage() {
  const [availableFields, setAvailableFields] = useState({});
  const [selectedFields, setSelectedFields] = useState([]);
  const [availableMerchants, setAvailableMerchants] = useState([]);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [format, setFormat] = useState('pdf');
  const [compress, setCompress] = useState(false);  // Changed to false for better browser compatibility
  const [pageSize, setPageSize] = useState('A4');
  const [reportName, setReportName] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [formats, setFormats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showMerchantSelector, setShowMerchantSelector] = useState(false);

  useEffect(() => {
    fetchAvailableFields();
    fetchFormats();
    fetchAvailableMerchants();
  }, []);

  const fetchAvailableMerchants = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/reports/merchants/`);
      if (response.ok) {
        const data = await response.json();
        setAvailableMerchants(data.merchants);
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
    }
  };

  const fetchAvailableFields = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/reports/fields/`);
      if (response.ok) {
        const data = await response.json();
        setAvailableFields(data.available_fields);
      }
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  const fetchFormats = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/reports/formats/`);
      if (response.ok) {
        const data = await response.json();
        setFormats(data.formats);
      }
    } catch (error) {
      console.error('Error fetching formats:', error);
    }
  };

  const handleCategoryToggle = (category) => {
    const categoryFields = availableFields[category];
    const allSelected = categoryFields.every(field => selectedFields.includes(field));
    
    if (allSelected) {
      // Deselect all fields in category
      setSelectedFields(selectedFields.filter(f => !categoryFields.includes(f)));
    } else {
      // Select all fields in category
      const newFields = [...new Set([...selectedFields, ...categoryFields])];
      setSelectedFields(newFields);
    }
  };

  const handleFieldToggle = (field) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter(f => f !== field));
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  };

  const handleMerchantToggle = (merchant) => {
    if (selectedMerchants.find(m => m.id === merchant.id)) {
      setSelectedMerchants(selectedMerchants.filter(m => m.id !== merchant.id));
    } else {
      setSelectedMerchants([...selectedMerchants, merchant]);
    }
  };

  const handleSelectAllMerchants = () => {
    const filteredMerchants = availableMerchants.filter(merchant =>
      merchant.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      merchant.domain.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setSelectedMerchants(filteredMerchants);
  };

  const handleDeselectAllMerchants = () => {
    setSelectedMerchants([]);
  };

  const handleSelectAll = () => {
    const allFields = Object.values(availableFields).flat();
    setSelectedFields(allFields);
  };

  const handleDeselectAll = () => {
    setSelectedFields([]);
  };

  const filteredMerchants = availableMerchants.filter(merchant =>
    merchant.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    merchant.domain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePreview = async () => {
    if (selectedMerchants.length === 0) {
      alert('Please select at least one merchant');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/v1/reports/custom/preview/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selected_fields: selectedFields,
          merchant_url: selectedMerchants[0].url  // Use m.url which contains merchant_url
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      }
    } catch (error) {
      console.error('Error previewing:', error);
      alert('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (selectedMerchants.length === 0) {
      alert('Please select at least one merchant');
      return;
    }

    if (selectedFields.length === 0) {
      alert('Please select at least one field');
      return;
    }

    try {
      setLoading(true);
      const viewingTenantId = localStorage.getItem('viewing_tenant_id');
      const headers = { 'Content-Type': 'application/json' };
      if (viewingTenantId) headers['X-Tenant-ID'] = viewingTenantId;

      const response = await fetch(`${API_BASE}/api/v1/reports/custom/generate/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tenant_id: null,  // Changed from UUID string to null (tenant field is nullable)
          merchant_urls: selectedMerchants.map(m => m.url),  // Use m.url which contains merchant_url
          format: format,
          selected_fields: selectedFields,
          compress: compress,
          page_size: pageSize,
          report_name: reportName || `custom_report_${Date.now()}`
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const contentType = response.headers.get('Content-Type');
        const contentDisposition = response.headers.get('Content-Disposition');
        
        // Extract filename from Content-Disposition header
        let filename = `report.${format === 'excel' ? 'xlsx' : format}`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=['"]?([^'"\n;]+)['"]?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].trim();
          }
        }
        
        // Always download with correct filename (don't open in browser)
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        alert('Report generated successfully!');
      } else {
        const error = await response.json().catch(() => ({}));
        const msg = error.error || error.detail || response.statusText;
        const details = error.details ? `\n\n${error.details}` : '';
        alert(`Failed to generate report: ${msg}${details}`);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert(`Failed to generate report: ${error.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  const formatIcons = {
    pdf: <FileText className="w-5 h-5" />,
    excel: <FileSpreadsheet className="w-5 h-5" />,
    word: <FileText className="w-5 h-5" />,
    json: <FileJson className="w-5 h-5" />
  };

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Custom Report Builder</h1>
        <p className="text-gray-600 mt-2">
          Select fields and configure your custom merchant report
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Field Selection */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Select Fields</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSelectAll}
                  >
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDeselectAll}
                  >
                    <Square className="w-4 h-4 mr-1" />
                    Deselect All
                  </Button>
                </div>
              </div>
              <CardDescription>
                Choose which data fields to include in your report
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(availableFields).map(([category, fields]) => {
                  const allSelected = fields.every(f => selectedFields.includes(f));
                  const someSelected = fields.some(f => selectedFields.includes(f));

                  return (
                    <div key={category} className="border rounded-lg p-4">
                      <div 
                        className="flex items-center justify-between mb-3 cursor-pointer"
                        onClick={() => handleCategoryToggle(category)}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={allSelected} />
                          <h3 className="font-semibold text-lg capitalize">{category}</h3>
                          <Badge variant={someSelected ? "default" : "secondary"}>
                            {fields.filter(f => selectedFields.includes(f)).length}/{fields.length}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 ml-6">
                        {fields.map(field => (
                          <div 
                            key={field} 
                            className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                            onClick={() => handleFieldToggle(field)}
                          >
                            <Checkbox checked={selectedFields.includes(field)} />
                            <span className="text-sm text-gray-700">{field}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Configuration */}
        <div>
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle>Report Configuration</CardTitle>
              <CardDescription>
                Configure report format and options
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Merchant Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">
                    Select Merchants ({selectedMerchants.length} selected)
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMerchantSelector(!showMerchantSelector)}
                  >
                    {showMerchantSelector ? 'Hide' : 'Show'} Selector
                  </Button>
                </div>
                
                {showMerchantSelector && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    {/* Search */}
                    <div className="mb-3">
                      <input
                        type="text"
                        placeholder="Search merchants by name or domain..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full border rounded-md p-2 text-sm"
                      />
                    </div>
                    
                    {/* Selection Controls */}
                    <div className="flex gap-2 mb-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAllMerchants}
                      >
                        Select All ({filteredMerchants.length})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeselectAllMerchants}
                      >
                        Deselect All
                      </Button>
                    </div>
                    
                    {/* Merchant List */}
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {filteredMerchants.map(merchant => (
                        <div
                          key={merchant.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-white ${
                            selectedMerchants.find(m => m.id === merchant.id) ? 'bg-blue-50 border border-blue-200' : ''
                          }`}
                          onClick={() => handleMerchantToggle(merchant)}
                        >
                          <Checkbox
                            checked={selectedMerchants.find(m => m.id === merchant.id) !== undefined}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">
                              {merchant.company_name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {merchant.domain}
                            </div>
                          </div>
                          {merchant.mcc_code && (
                            <Badge variant="secondary" className="text-xs">
                              MCC {merchant.mcc_code}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {filteredMerchants.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No merchants found matching your search.
                      </p>
                    )}
                  </div>
                )}
                
                {/* Selected Merchants Summary */}
                {selectedMerchants.length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                    <div className="font-medium mb-1">Selected Merchants:</div>
                    <div className="flex flex-wrap gap-1">
                      {selectedMerchants.slice(0, 5).map(merchant => (
                        <Badge key={merchant.id} variant="secondary" className="text-xs">
                          {merchant.company_name}
                        </Badge>
                      ))}
                      {selectedMerchants.length > 5 && (
                        <Badge variant="secondary" className="text-xs">
                          +{selectedMerchants.length - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Format</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(formats).map(([key, info]) => (
                    <button
                      key={key}
                      onClick={() => setFormat(key)}
                      className={`flex items-center gap-2 p-3 border rounded-lg transition-all ${
                        format === key 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {formatIcons[key]}
                      <div className="text-left">
                        <div className="text-sm font-medium">{key.toUpperCase()}</div>
                        <div className="text-xs text-gray-500">{info.extension}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Page Size */}
              {(format === 'pdf' || format === 'word') && (
                <div>
                  <label className="block text-sm font-medium mb-2">Page Size</label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={pageSize}
                    onChange={(e) => setPageSize(e.target.value)}
                  >
                    <option value="A4">A4</option>
                    <option value="letter">Letter</option>
                  </select>
                </div>
              )}

              {/* Compression */}
              {format !== 'json' && (
                <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <Checkbox 
                    checked={compress} 
                    onCheckedChange={setCompress}
                  />
                  <div className="flex-1">
                    <label className="text-sm font-medium cursor-pointer">
                      Compress file (.gz format)
                    </label>
                    <p className="text-xs text-gray-600">
                      Note: Compressed files must be extracted before viewing. 
                      Leave unchecked for PDFs to view in browser.
                    </p>
                  </div>
                </div>
              )}

              {/* Report Name */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Report Name (optional)
                </label>
                <Input
                  placeholder="my_custom_report"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                />
              </div>

              {/* Stats */}
              <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Selected Fields:</span>
                  <span className="font-medium">{selectedFields.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Selected Merchants:</span>
                  <span className="font-medium">{selectedMerchants.length}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <Button 
                  className="w-full" 
                  onClick={handlePreview}
                  disabled={loading || selectedFields.length === 0 || selectedMerchants.length === 0}
                  variant="outline"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Fields
                </Button>
                
                <Button 
                  className="w-full" 
                  onClick={handleGenerate}
                  disabled={loading || selectedFields.length === 0 || selectedMerchants.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {loading ? 'Generating...' : 'Generate Report'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Panel */}
      {preview && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Field Preview</CardTitle>
            <CardDescription>
              Preview of data that will be included in the report
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-96 text-sm">
              {JSON.stringify(preview.preview_data, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
